/**
 * 외부 서비스 연동 공통 Mixin
 * HospitalInfoM.vue, HospitalInfoW.vue에서 공통으로 사용
 */
import { openDialog } from "@/utils/utils";

export default {
  data() {
    return {
      // 외부 서비스 연동 관련
      emrId: '', // 기존 EMR 연동 ID (수정 시 사용)
      externalServiceTypes: [], // 외부 서비스 타입 목록
      selectedServiceType: '', // 선택된 서비스 타입
      externalServices: [], // 연동 서비스(제공자) 목록
      selectedService: '', // 선택된 연동 서비스(제공자)
      apiKey: '', // API 키
      originalApiKey: '', // EMR에서 조회한 원본 API 키 (변경 감지용)
      clientId: '', // 클라이언트 ID
      clientSecret: '', // 클라이언트 시크릿
      showApiKey: false, // apiKey 표시 여부
      showClientId: false, // clientId 표시 여부
      showClientSecret: false, // clientSecret 표시 여부
      showServiceTypeInput: false, // 서비스 타입 입력 필드 표시 여부
      newServiceTypeName: '', // 새 서비스 타입명
      showProviderInput: false, // 제공자 입력 필드 표시 여부
      newProviderName: '', // 새 제공자명

      // 스케줄 관련 (DOCTOR_PALETTE 전용)
      scheduleTypes: [], // 스케줄 종류 목록
      selectedScheduleType: '', // 선택된 스케줄 종류
      useExternalSchedule: false, // 예약연동활성화 여부
    };
  },
  computed: {
    // 외부서비스 구분이 "예약"이고 연동 서비스가 "DOCTOR_PALETTE"일 때 스케줄 종류 표시
    showScheduleTypeSelect() {
      // 선택된 서비스 타입의 name이 "예약"인지 확인
      const selectedType = this.externalServiceTypes.find(type => type._id === this.selectedServiceType);
      const isReservationType = selectedType && selectedType.name === '예약';

      // 선택된 연동 서비스의 code가 "DOCTOR_PALETTE"인지 확인
      const selectedProvider = this.externalServices.find(service => service._id === this.selectedService);
      const isDoctorPalette = selectedProvider && selectedProvider.code === 'DOCTOR_PALETTE';

      return isReservationType && isDoctorPalette;
    }
  },
  methods: {
    // 외부 서비스 필드 초기화
    initExternalServiceValues() {
      this.emrId = '';
      this.selectedServiceType = '';
      this.externalServices = [];
      this.selectedService = '';
      this.apiKey = '';
      this.originalApiKey = '';
      this.clientId = '';
      this.clientSecret = '';
      this.showApiKey = false;
      this.showClientId = false;
      this.showClientSecret = false;
      this.showServiceTypeInput = false;
      this.newServiceTypeName = '';
      this.showProviderInput = false;
      this.newProviderName = '';
      // 스케줄 관련 초기화
      this.scheduleTypes = [];
      this.selectedScheduleType = '';
      this.useExternalSchedule = false;
    },

    // 외부 서비스 타입 목록 조회
    async getExternalServiceTypes() {
      try {
        const response = await this.$apiInstance.get('/admin/external-services/types/active');
        if (response.status === 200 || response.status === 201) {
          this.externalServiceTypes = response.data;
        }
      } catch (error) {
        console.log('외부 서비스 타입 조회 실패:', error);
      }
    },

    // EMR 연동 정보 조회
    async getEmrIntegration(hospitalId) {
      try {
        const response = await this.$apiInstance.get(`/emr/hospital/${hospitalId}`);
        if (response.status === 200 || response.status === 201) {
          const emrData = response.data;

          // EMR ID 저장 (수정 시 사용)
          this.emrId = emrData._id || '';

          // 서비스 타입 설정 (serviceType 객체에서 _id 추출)
          if (emrData.serviceType && emrData.serviceType._id) {
            this.selectedServiceType = emrData.serviceType._id;

            // 서비스 타입에 맞는 제공자 목록 로드 (스케줄 조회 제외)
            await this.loadProvidersWithoutSchedule();

            // 제공자 설정 (serviceProvider 객체에서 _id 추출)
            if (emrData.serviceProvider && emrData.serviceProvider._id) {
              this.selectedService = emrData.serviceProvider._id;

              // 제공자 설정 후 스케줄 조회
              await this.$nextTick();
              if (this.showScheduleTypeSelect) {
                await this.getDoctorPaletteSchedule();

                // 기존 스케줄 정보 설정
                if (emrData.schedule && emrData.schedule.id) {
                  this.selectedScheduleType = emrData.schedule.id;
                }
              }
            }
          }

          // credentials 정보 설정
          if (emrData.credentials) {
            this.apiKey = emrData.credentials.apiKey || '';
            this.originalApiKey = emrData.credentials.apiKey || ''; // 원본 저장 (변경 감지용)
            this.clientId = emrData.credentials.clientId || '';
            this.clientSecret = emrData.credentials.clientSecret || '';
          }

          // 예약연동활성화 설정
          this.useExternalSchedule = emrData.useExternalSchedule === 'Y';
        }
      } catch (error) {
        // 404 또는 500 등 에러 발생 시 기본값 설정
        // EMR 정보가 없으면 외부 서비스 타입/제공자 0번째 항목 선택
        this.emrId = '';
        this.useExternalSchedule = false;
        if (this.externalServiceTypes && this.externalServiceTypes.length > 0) {
          this.selectedServiceType = this.externalServiceTypes[0]._id;
          await this.onServiceTypeChange();
        }
      }
    },

    // 제공자 목록만 로드 (스케줄 조회 제외 - EMR 조회 시 사용)
    async loadProvidersWithoutSchedule() {
      this.selectedService = '';
      this.externalServices = [];

      if (this.selectedServiceType) {
        try {
          const selectedType = this.externalServiceTypes.find(type => type._id === this.selectedServiceType);
          if (!selectedType) return;

          const response = await this.$apiInstance.get(`/admin/external-services/providers/by-type/${selectedType.code}`);
          if (response.status === 200 || response.status === 201) {
            this.externalServices = response.data;
          }
        } catch (error) {
          console.log('제공자 목록 조회 실패:', error);
        }
      }
    },

    // 서비스 타입 선택 시 제공자 목록 조회
    async onServiceTypeChange() {
      this.selectedService = '';
      this.externalServices = [];
      // 스케줄도 초기화
      this.scheduleTypes = [];
      this.selectedScheduleType = '';

      if (this.selectedServiceType) {
        try {
          // 선택된 타입 객체에서 code 추출
          const selectedType = this.externalServiceTypes.find(type => type._id === this.selectedServiceType);
          if (!selectedType) return;

          const response = await this.$apiInstance.get(`/admin/external-services/providers/by-type/${selectedType.code}`);
          if (response.status === 200 || response.status === 201) {
            this.externalServices = response.data;
            // 제공자 목록의 0번째 항목을 기본 선택
            if (this.externalServices.length > 0) {
              this.selectedService = this.externalServices[0]._id;
              // 제공자 선택 후 스케줄 조회
              await this.$nextTick();
              await this.onServiceChange();
            }
          }
        } catch (error) {
          console.log('제공자 목록 조회 실패:', error);
        }
      }
    },

    // 연동 서비스 변경 시 호출
    async onServiceChange() {
      // 스케줄 초기화 후 조건에 맞으면 조회
      this.scheduleTypes = [];
      this.selectedScheduleType = '';

      // Vue의 nextTick을 사용하여 computed 속성이 갱신된 후 스케줄 조회
      await this.$nextTick();
      if (this.showScheduleTypeSelect) {
        await this.getDoctorPaletteSchedule();
      }
    },

    // 서비스 타입 추가 입력 필드 표시
    openServiceTypeModal() {
      this.showServiceTypeInput = true;
      this.newServiceTypeName = '';
    },

    // 서비스 타입 추가 취소
    cancelServiceTypeInput() {
      this.showServiceTypeInput = false;
      this.newServiceTypeName = '';
    },

    // 서비스 타입 저장
    async saveServiceType() {
      if (!this.newServiceTypeName.trim()) {
        await openDialog("서비스 타입명을 입력해주세요.", "warning", "확인");
        return;
      }

      try {
        const response = await this.$apiInstance.post('/admin/external-services/types', {
          name: this.newServiceTypeName.trim(),
          description: '',
          isActive: 'Y'
        });

        if (response.status === 200 || response.status === 201) {
          await openDialog("서비스 타입이 생성되었습니다.", "success", "확인");

          // 입력 필드 숨기기
          this.showServiceTypeInput = false;
          this.newServiceTypeName = '';

          // 목록 갱신
          await this.getExternalServiceTypes();

          // 새로 생성된 타입 자동 선택
          this.selectedServiceType = response.data._id;
          await this.onServiceTypeChange();
        }
      } catch (error) {
        console.log('서비스 타입 생성 실패:', error);
        await openDialog("서비스 타입 생성에 실패했습니다.", "error", "확인");
      }
    },

    // 제공자 추가 입력 필드 표시
    openProviderModal() {
      this.showProviderInput = true;
      this.newProviderName = '';
    },

    // 제공자 추가 취소
    cancelProviderInput() {
      this.showProviderInput = false;
      this.newProviderName = '';
    },

    // 제공자 저장
    async saveProvider() {
      if (!this.newProviderName.trim()) {
        await openDialog("제공자명을 입력해주세요.", "warning", "확인");
        return;
      }

      const selectedType = this.externalServiceTypes.find(type => type._id === this.selectedServiceType);
      if (!selectedType) {
        await openDialog("서비스 타입을 먼저 선택해주세요.", "warning", "확인");
        return;
      }

      try {
        const response = await this.$apiInstance.post('/admin/external-services/providers', {
          name: this.newProviderName.trim(),
          serviceTypeCode: selectedType.code,
          description: ''
        });

        if (response.status === 200 || response.status === 201) {
          await openDialog("제공자가 생성되었습니다.", "success", "확인");

          // 입력 필드 숨기기
          this.showProviderInput = false;
          this.newProviderName = '';

          // 제공자 목록 갱신
          await this.onServiceTypeChange();

          // 새로 생성된 제공자 자동 선택
          this.selectedService = response.data._id;
        }
      } catch (error) {
        console.log('제공자 생성 실패:', error);
        await openDialog("제공자 생성에 실패했습니다.", "error", "확인");
      }
    },

    // apiKey 표시/숨김
    toggleShowApiKey() {
      this.showApiKey = !this.showApiKey;
    },

    // clientId 표시/숨김
    toggleShowClientId() {
      this.showClientId = !this.showClientId;
    },

    // clientSecret 표시/숨김
    toggleShowClientSecret() {
      this.showClientSecret = !this.showClientSecret;
    },

    // DOCTOR_PALETTE 스케줄 종류 조회 (자동 조회용 - 연동 서비스 변경 시)
    // 이미 EMR이 있는 경우에만 자동 조회 (수정 화면에서)
    async getDoctorPaletteSchedule() {
      // 스케줄 초기화
      this.scheduleTypes = [];
      this.selectedScheduleType = '';

      // 조건 확인: 외부서비스 구분이 "예약"이고 연동 서비스가 "DOCTOR_PALETTE"일 때만 조회
      if (!this.showScheduleTypeSelect || !this.hospitalId) {
        return;
      }

      // emrId가 있을 때만 자동 조회 (이미 EMR이 저장된 경우)
      if (!this.emrId) {
        return;
      }

      try {
        // 닥터팔레트 전용 API 키로 스케줄 조회
        const dpApiKey = process.env.VUE_APP_DOCTOR_PALETTE_API_KEY;
        if (!dpApiKey) {
          alert('Doctor Palette API Key가 설정되지 않았습니다. 환경변수를 확인해주세요.');
          return;
        }

        const response = await this.$apiInstance.get(`/doctor-palette/schedule?hospitalId=${this.hospitalId}`, {
          headers: {
            'x-api-key': dpApiKey
          }
        });
        if (response.status === 200 || response.status === 201) {
          this.scheduleTypes = response.data || [];
          // 기본 선택 없음 - EMR 조회 시 기존 schedule 정보가 있을 때만 선택됨
        }
      } catch (error) {
        console.log('스케줄 종류 조회 실패:', error);
        this.scheduleTypes = [];

        // 에러 메시지에서 401 확인 (인증 실패)
        const errorMessage = error.response?.data?.message || error.message || '';
        if (errorMessage.includes('401')) {
          await openDialog("인증에 실패했습니다. API Key를 다시 입력 후 스케줄 조회 버튼을 눌러주세요.", "error", "확인");
        }
      }
    },

    // DOCTOR_PALETTE 스케줄 종류 수동 조회 (조회 버튼 클릭용)
    async fetchScheduleTypes() {
      // 외부 서비스 구분 및 연동 서비스 검증
      if (!this.selectedServiceType) {
        await openDialog("외부 서비스 구분을 선택해주세요.", "warning", "확인");
        return;
      }
      if (!this.selectedService) {
        await openDialog("연동 서비스를 선택해주세요.", "warning", "확인");
        return;
      }

      // apiKey 검증
      const currentApiKey = (this.apiKey || '').trim();
      if (!currentApiKey) {
        await openDialog("API Key를 입력해주세요.", "warning", "확인");
        return;
      }

      // 스케줄 초기화
      this.scheduleTypes = [];
      this.selectedScheduleType = '';

      try {
        // 닥터팔레트 고유 외부 키
        const dpApiKey = process.env.VUE_APP_DOCTOR_PALETTE_API_KEY;
        if (!dpApiKey) {
          await openDialog("Doctor Palette API Key가 설정되지 않았습니다. 환경변수를 확인해주세요.", "error", "확인");
          return;
        }

        // 사용자가 입력한 apiKey를 직접 전달하여 스케줄 조회
        const response = await this.$apiInstance.post('/doctor-palette/schedule', {
          doctorPaletteApiKey: currentApiKey
        }, {
          headers: {
            'x-api-key': dpApiKey
          }
        });

        if (response.status === 200 || response.status === 201) {
          this.scheduleTypes = response.data || [];
          if (this.scheduleTypes.length === 0) {
            await openDialog("조회된 스케줄 종류가 없습니다.", "info", "확인");
          } else {
            await openDialog(`${this.scheduleTypes.length}건 조회완료. 스케줄을 선택해주세요.`, "success", "확인");
          }
        }
      } catch (error) {
        console.log('스케줄 종류 조회 실패:', error);
        this.scheduleTypes = [];

        // 에러 메시지에서 401 확인 (인증 실패)
        const errorMessage = error.response?.data?.message || error.message || '';
        if (errorMessage.includes('401')) {
          await openDialog("인증에 실패했습니다. API Key를 확인 후 다시 시도해주세요.", "error", "확인");
        } else {
          await openDialog("스케줄 종류 조회에 실패했습니다.", "error", "확인");
        }
      }
    },

    // 스케줄 조회를 위한 EMR 저장
    // method: 'POST' (새 EMR 생성) 또는 'PUT' (기존 EMR 수정)
    async saveEmrForScheduleQuery(method) {
      console.log('=== saveEmrForScheduleQuery 호출 ===');
      console.log('method:', method);
      console.log('emrId:', this.emrId);

      const credentials = {};
      // apiKey는 빈값도 저장 (기존 값 삭제 용도)
      credentials.apiKey = (this.apiKey || '').trim();
      if (this.clientId) credentials.clientId = this.clientId;
      if (this.clientSecret) credentials.clientSecret = this.clientSecret;

      if (method === 'PUT' && this.emrId) {
        // PUT 요청 - 기존 EMR 수정
        const updateData = {
          serviceTypeId: this.selectedServiceType,
          serviceProviderId: this.selectedService,
          credentials: credentials,
          useExternalSchedule: this.useExternalSchedule ? 'Y' : 'N',
        };

        const response = await this.$apiInstance.put(`/emr/${this.emrId}`, updateData);
        // 저장 성공 후 originalApiKey 업데이트
        this.originalApiKey = this.apiKey.trim();
        return response.data;
      } else if (method === 'POST') {
        // POST 요청 - 새 EMR 생성
        // hospitalName, hospitalCno는 부모 컴포넌트에서 제공해야 함
        const emrData = {
          hospitalId: this.hospitalId,
          hospitalName: this.hospitalName || '',
          hospitalCno: this.businessNumber || '',
          serviceTypeId: this.selectedServiceType,
          serviceProviderId: this.selectedService,
          credentials: credentials,
          useExternalSchedule: this.useExternalSchedule ? 'Y' : 'N',
          isActive: 'Y'
        };

        const response = await this.$apiInstance.post('/emr', emrData);
        // 새로 생성된 EMR ID 저장
        if (response.data && response.data._id) {
          this.emrId = response.data._id;
        }
        // 저장 성공 후 originalApiKey 업데이트
        this.originalApiKey = this.apiKey.trim();
        return response.data;
      }
    },

    // EMR 연동 정보 저장 (생성 또는 수정)
    async saveEmrIntegration(hospitalId, hospitalData) {
      // credentials 객체 구성
      const credentials = {};
      // apiKey는 빈값도 저장 (기존 값 삭제 용도)
      credentials.apiKey = (this.apiKey || '').trim();
      if (this.clientId) credentials.clientId = this.clientId;
      if (this.clientSecret) credentials.clientSecret = this.clientSecret;

      // schedule 객체 구성 (DOCTOR_PALETTE인 경우)
      let schedule = null;
      if (this.showScheduleTypeSelect && this.selectedScheduleType) {
        const selectedSchedule = this.scheduleTypes.find(s => s.id === this.selectedScheduleType);
        if (selectedSchedule) {
          schedule = {
            id: selectedSchedule.id,
            name: selectedSchedule.name,
            slotMinute: selectedSchedule.slotMinute || 'MIN_30'
          };
        }
      }

      // 기존 EMR이 있으면 PUT (수정), 없으면 POST (생성)
      if (this.emrId) {
        // PUT 요청 - 수정
        const updateData = {
          serviceTypeId: this.selectedServiceType,
          serviceProviderId: this.selectedService,
          credentials: credentials,
          useExternalSchedule: this.useExternalSchedule ? 'Y' : 'N',
        };

        // schedule 정보가 있으면 추가
        if (schedule) {
          updateData.schedule = schedule;
        }

        const response = await this.$apiInstance.put(`/emr/${this.emrId}`, updateData);
        return response.data;
      } else {
        // POST 요청 - 생성
        const emrData = {
          hospitalId: hospitalId,
          hospitalName: hospitalData.name,
          hospitalCno: hospitalData.cNo,
          serviceTypeId: this.selectedServiceType,
          serviceProviderId: this.selectedService,
          credentials: credentials,
          useExternalSchedule: this.useExternalSchedule ? 'Y' : 'N',
          isActive: 'Y'
        };

        // schedule 정보가 있으면 추가
        if (schedule) {
          emrData.schedule = schedule;
        }

        const response = await this.$apiInstance.post('/emr', emrData);
        return response.data;
      }
    },

    // 외부 서비스 등기화 (사용하지 않는 메서드지만 호환성을 위해 유지)
    async registerExternalService() {
      // 유효성 검사
      if (!this.selectedServiceType) {
        await openDialog("외부 서비스 구분을 선택해주세요.", "warning", "확인");
        return;
      }
      if (!this.selectedService) {
        await openDialog("연동 서비스를 선택해주세요.", "warning", "확인");
        return;
      }
      if (!this.apiKey) {
        await openDialog("API 키를 입력해주세요.", "warning", "확인");
        return;
      }

      try {
        const response = await this.$apiInstance.post(`/companies/${this.hospitalId}/external-services`, {
          serviceTypeId: this.selectedServiceType,
          serviceId: this.selectedService,
          apiKey: this.apiKey,
        });

        if (response.status === 200 || response.status === 201) {
          await openDialog("외부 서비스가 등기화되었습니다.", "success", "확인");
        }
      } catch (error) {
        console.log('외부 서비스 등기화 실패:', error);
        await openDialog("외부 서비스 등기화에 실패했습니다.", "error", "확인");
      }
    },
  }
};
